/**
 * router/index.ts
 * Route table for the RIGX Operador PWA.
 *
 * v1 scope is "fallas de flota" (LOCX) — see
 * openspec/changes/archive/2026-07-20-add-locx-fleet-failure-reports (backend)
 * and project memory project_locx_operational_app_future_need. PR2b of
 * add-locx-trip-fuel-tracking adds "gerenciamiento de viaje" and "carga de
 * combustible" on top. EPP shipped as /epp (nomi-epp-firma).
 *   - /login              (public, fullscreen, no shell)
 *   - /                    (home, inside AppLayout — shell-nested)
 *   - /fallas              (mis fallas asignadas — list)
 *   - /fallas/nueva        (reportar una falla nueva)
 *   - /fallas/:id          (detail + FSM transitions)
 *   - /viajes              (mis viajes — list)
 *   - /viajes/nuevo        (solicitar un viaje nuevo)
 *   - /viajes/:id          (detail, read-only)
 *   - /combustible         (cargas de combustible — list)
 *   - /combustible/nueva   (registrar una carga nueva)
 *   - /combustible/:id     (detail + firma)
 *   - /documentos          (mis documentos + certificado laboral)
 *   - /epp                 (mis EPP: entregas, firma y constancia)
 *   - /calendario          (mi diagrama)
 *   - /ausencias           (mis ausencias — list)
 *   - /ausencias/nueva     (solicitar una ausencia nueva)
 *   - /organigrama         (organigrama, read-only)
 *   - /solicitudes-enviadas (mis solicitudes de servicio enviadas, read-only)
 *   - /pedidos-repuestos   (mis pedidos de repuestos, read-only)
 *   - /escanear            (in-app QR scanner, full-screen camera)
 *   - /qr/:token           (a unit's documents and checklist, from a sticker)
 *
 * The 5 above (nomi-autoservicio-empleado Tier A) moved here from
 * apps/frontend 2026-09-14 — architecture correction: employee self-service
 * belongs in this app (app.rigx.sofistic.io), apps/frontend (rigx.sofistic.io) is
 * the admin/aprobación side only (see AttendanceEventsReportView.vue there,
 * unchanged). Same `/rrhh/self/*` backend routes, untouched.
 *
 * Guard: same posture as apps/frontend's scoped guard (FS-13), trimmed down.
 * Unauthenticated → /login; authenticated hitting /login → /. A hub route
 * (Campo/Yo) the user's capabilities leave empty → / (operator-tabs-
 * by-role). Nested screens stay ungated here: the backend guard answers 403
 * and the hubs never link to them for a user without the capability.
 */
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { isAuthenticated } from '../lib/session.js';
import { currentCapabilities } from '../lib/capabilities.js';
import { isHubVisible, type HubSection } from '../lib/service-registry.js';

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: { public: true },
  },
  {
    // operator-qr-scanner — the in-app scanner. Full-screen camera, outside
    // AppLayout like /voz; a RigX sticker read here opens qr-unit below.
    path: '/escanear',
    name: 'qr-scan',
    component: () => import('../views/QrScanView.vue'),
  },
  {
    // locx-voz-multi-intencion (design.md D14) — app-shell mic entry point
    // (AppLayout.vue's topbar), reachable from anywhere. Outside AppLayout on
    // purpose, same posture as /qr/:token above: the recording stage (screen
    // 2) is a full-screen takeover, not a screen inside the normal chrome —
    // AppLayout's fixed topbar would sit on top of it otherwise. Requires
    // login (no `meta.public`), same guard as every other route below.
    // `assetId` query param (optional) forwards the scanned-unit asset into
    // the falla confirmation screen (task 9.3) — never a route param, since
    // most dictations (repuesto, or a falla with no scanned unit) have none.
    path: '/voz',
    name: 'voice-intake',
    component: () => import('../views/VoiceIntakeView.vue'),
  },
  {
    path: '/',
    component: () => import('../components/AppLayout.vue'),
    children: [
      {
        // Hoy — the worker's day at a glance (operator-home-hoy). `hub` meta
        // marks a tab of its own in the bottom TabBar.
        path: '',
        name: 'home',
        component: () => import('../views/HomeView.vue'),
        meta: { hub: true },
      },
      {
        path: 'campo',
        name: 'hub-campo',
        component: () => import('../views/HubView.vue'),
        props: { section: 'campo' },
        meta: { hub: true, hubSection: 'campo' },
      },
      {
        path: 'yo',
        name: 'hub-yo',
        component: () => import('../views/HubView.vue'),
        props: { section: 'yo' },
        meta: { hub: true, hubSection: 'yo' },
      },
      {
        // operator-scan-tab — the Equipo tab became the scanner; its only
        // entry (Organigrama) now lives in Yo. Old links land there.
        path: 'equipo',
        redirect: { name: 'hub-yo' },
      },
      {
        // fleet-qr-compliance PR4 — a unit's documents and checklist. Reached
        // from a printed sticker (the QR encodes this URL, scanned with the
        // phone's camera) or from the in-app scanner (/escanear). Requires
        // login (require-login-for-qr, 2026-08-20): a logged-out scan is
        // bounced to /login?redirect=/qr/:token. Inside AppLayout since
        // operator-qr-scanner, so the fixed tab bar is there too.
        path: 'qr/:token',
        name: 'qr-unit',
        component: () => import('../views/QrUnitView.vue'),
      },
      {
        path: 'fallas',
        name: 'fallas-list',
        component: () => import('../views/FallasListView.vue'),
      },
      {
        // Registered BEFORE fallas/:id — "nueva" must never match as an :id
        // (same ordering requirement the backend uses for /rrhh/persons/me
        // vs. /rrhh/persons/:id).
        path: 'fallas/nueva',
        name: 'falla-nueva',
        component: () => import('../views/ReportarFallaView.vue'),
      },
      {
        path: 'fallas/:id',
        name: 'falla-detail',
        component: () => import('../views/FallaDetailView.vue'),
      },
      {
        path: 'viajes',
        name: 'trips-list',
        component: () => import('../views/TripsListView.vue'),
      },
      {
        // Registered BEFORE viajes/:id — same "nuevo" vs. :id ordering
        // requirement as fallas/nueva above.
        path: 'viajes/nuevo',
        name: 'trip-new',
        component: () => import('../views/NuevoViajeView.vue'),
      },
      {
        path: 'viajes/:id',
        name: 'trip-detail',
        component: () => import('../views/TripDetailView.vue'),
      },
      {
        path: 'combustible',
        name: 'fuel-loads-list',
        component: () => import('../views/FuelLoadsListView.vue'),
      },
      {
        // Registered BEFORE combustible/:id — same ordering requirement.
        path: 'combustible/nueva',
        name: 'fuel-load-new',
        component: () => import('../views/NuevaCargaView.vue'),
      },
      {
        path: 'combustible/:id',
        name: 'fuel-load-detail',
        component: () => import('../views/FuelLoadDetailView.vue'),
      },
      {
        path: 'documentos',
        name: 'mis-documentos',
        component: () => import('../views/MisDocumentosView.vue'),
      },
      {
        path: 'epp',
        name: 'mis-epp',
        component: () => import('../views/MisEppView.vue'),
      },
      // odd/tasks/operator-panol.md — the pañolero on the phone (static 'recibir' before any param).
      {
        path: 'panol',
        name: 'panol',
        component: () => import('../views/PanolView.vue'),
      },
      {
        path: 'panol/recibir',
        name: 'panol-recibir',
        component: () => import('../views/PanolRecibirView.vue'),
      },
      {
        path: 'calendario',
        name: 'mi-calendario',
        component: () => import('../views/MiCalendarioView.vue'),
      },
      {
        path: 'ausencias',
        name: 'ausencias-list',
        component: () => import('../views/MisAusenciasView.vue'),
      },
      {
        // Registered BEFORE any ausencias/:id — same "nueva" vs. :id ordering
        // requirement as fallas/nueva above (no :id detail route exists yet
        // for an individual ausencia — the list itself is the full history).
        path: 'ausencias/nueva',
        name: 'ausencia-nueva',
        component: () => import('../views/NuevaAusenciaView.vue'),
      },
      {
        path: 'organigrama',
        name: 'organigrama',
        component: () => import('../views/OrganigramaView.vue'),
      },
      {
        // locx-voz-servicio-cross-tenant PR2 (D5) — "Mis solicitudes
        // enviadas": read-only, no sub-routes (no :id detail — the list
        // itself is the full history, same posture as ausencias-list).
        path: 'solicitudes-enviadas',
        name: 'solicitudes-enviadas',
        component: () => import('../views/MisSolicitudesEnviadasView.vue'),
      },
      {
        // operator-mis-pedidos-repuestos — "Mis pedidos de repuestos": the
        // `purchase_case`/repuesto intent's own equivalent of
        // solicitudes-enviadas above. Read-only, no sub-routes — same
        // "the list itself is the full history" posture.
        path: 'pedidos-repuestos',
        name: 'pedidos-repuestos',
        component: () => import('../views/MisPedidosRepuestosView.vue'),
      },
    ],
  },
  // Catch-all: unknown paths resolve to home (guard bounces to /login if unauthenticated).
  { path: '/:pathMatch(.*)*', redirect: { name: 'home' } },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const authed = isAuthenticated.value;

  if (!to.meta['public'] && !authed) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  if (to.name === 'login' && authed) {
    return { name: 'home' };
  }
  // operator-tabs-by-role — a hub the user has no service in (e.g. Campo for
  // an HR-only user reached through an old link) falls back to Hoy.
  const hubSection = to.meta['hubSection'] as HubSection | undefined;
  if (hubSection && !isHubVisible(hubSection, currentCapabilities.value)) {
    return { name: 'home' };
  }
  return true;
});
